import type { HrMonitorSnapshot } from '../../lib/hrMonitoring';

interface MonitorTabProps {
    hrMonitor: HrMonitorSnapshot;
}

export function MonitorTab({ hrMonitor }: MonitorTabProps) {
    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
                <div>
                    <h2 className="text-lg font-bold text-slate-900">{hrMonitor.evalYear}년 HR 모니터링 대시보드</h2>
                    <p className="text-sm text-slate-500">Assign · 분기 Confirm · 다면 풀/집계 · Calibration 관대화를 한눈에 점검합니다.</p>
                </div>
                <div className="text-xs font-semibold bg-slate-100 border border-slate-200 px-3 py-2 rounded-xl text-slate-700">
                    임직원 {hrMonitor.employeeCount}명 · 전체 계정 {hrMonitor.memberCount}명
                </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                    { label: 'Assign 승인', value: hrMonitor.assign.approved, sub: `대기 ${hrMonitor.assign.registered} · 반려 ${hrMonitor.assign.rejected}` },
                    { label: '분기 Confirm', value: `${hrMonitor.quarters.confirmRate}%`, sub: `${hrMonitor.quarters.confirmedCells}/${hrMonitor.quarters.totalCells} 분기` },
                    { label: '풀 확정', value: `${hrMonitor.peer.poolsConfirmed}/${hrMonitor.peer.poolsTotal}`, sub: `다면 수신 ${hrMonitor.peer.membersWithReport}명` },
                    { label: '성과 EX 비율', value: `${hrMonitor.grades.exRate}%`, sub: hrMonitor.grades.isLenient ? '기준 20% 초과' : '기준 이내' },
                ].map((kpi) => (
                    <div key={kpi.label} className="bg-white border border-slate-200 rounded-2xl p-4 shadow-sm">
                        <p className="text-[11px] font-bold text-slate-500">{kpi.label}</p>
                        <p className="text-2xl font-extrabold text-slate-900 mt-1">{kpi.value}</p>
                        <p className="text-[11px] text-slate-400 mt-1">{kpi.sub}</p>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-1 bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    <h3 className="font-bold text-sm text-slate-900 mb-3">경보 / 액션</h3>
                    <div className="space-y-2">
                        {hrMonitor.alerts.map((a, idx) => (
                            <div
                                key={idx}
                                className={`rounded-xl border px-3 py-2.5 text-xs ${
                                    a.level === 'danger' ? 'bg-rose-50 border-rose-200 text-rose-900' :
                                    a.level === 'warn' ? 'bg-amber-50 border-amber-200 text-amber-900' :
                                    'bg-slate-50 border-slate-200 text-slate-700'
                                }`}
                            >
                                <p className="font-bold">{a.title}</p>
                                <p className="mt-0.5 leading-relaxed opacity-90">{a.detail}</p>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    <h3 className="font-bold text-sm text-slate-900 mb-3">등급 분포 (팀원)</h3>
                    <div className="grid grid-cols-2 gap-4 text-xs">
                        <div>
                            <p className="font-bold text-slate-500 mb-2">성과</p>
                            {['EX', 'GD', 'NI'].map((g) => (
                                <div key={`p-${g}`} className="flex justify-between py-1 border-b border-slate-100">
                                    <span>{g}</span>
                                    <span className="font-extrabold">{hrMonitor.grades.perf[g] || 0}명</span>
                                </div>
                            ))}
                        </div>
                        <div>
                            <p className="font-bold text-slate-500 mb-2">일하는 방식</p>
                            {['EX', 'GD', 'NI'].map((g) => (
                                <div key={`b-${g}`} className="flex justify-between py-1 border-b border-slate-100">
                                    <span>{g}</span>
                                    <span className="font-extrabold">{hrMonitor.grades.behavior[g] || 0}명</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <p className="text-[11px] text-violet-700 mt-3 font-semibold">
                        다면 추천≠방식 등급: {hrMonitor.peer.divergeFromSuggest}명
                    </p>
                </div>

                <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
                    <h3 className="font-bold text-sm text-slate-900 mb-3">과제 Category / 평가군</h3>
                    <div className="space-y-3 text-xs">
                        <div>
                            <p className="font-bold text-slate-500 mb-1">과제 Category</p>
                            {Object.keys(hrMonitor.assign.byCategory).length === 0 && (
                                <p className="text-slate-400">등록된 과제 없음</p>
                            )}
                            {Object.entries(hrMonitor.assign.byCategory).map(([k, v]) => (
                                <div key={k} className="flex justify-between py-1 border-b border-slate-100">
                                    <span>{k}</span>
                                    <span className="font-extrabold">{v}건</span>
                                </div>
                            ))}
                        </div>
                        <div>
                            <p className="font-bold text-slate-500 mb-1">평가군</p>
                            {hrMonitor.byEvalGroup.map((g) => (
                                <div key={g.group} className="flex justify-between py-1 border-b border-slate-100">
                                    <span>{g.group}</span>
                                    <span className="font-extrabold">{g.members}명 · EX {g.exRate}%</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="font-bold text-sm text-slate-900">부서별 진행 현황</h3>
                    <span className="text-[11px] text-slate-400">승인과제 · 풀확정 · 다면수신 · EX</span>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                        <thead className="bg-slate-50 text-slate-500 font-bold">
                            <tr>
                                <th className="px-4 py-3">부서</th>
                                <th className="px-4 py-3 text-right">인원</th>
                                <th className="px-4 py-3 text-right">승인과제</th>
                                <th className="px-4 py-3 text-right">풀확정</th>
                                <th className="px-4 py-3 text-right">다면수신</th>
                                <th className="px-4 py-3 text-right">EX비율</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {hrMonitor.byDept.length === 0 && (
                                <tr>
                                    <td colSpan={6} className="px-4 py-8 text-center text-slate-400">표시할 부서 데이터가 없습니다.</td>
                                </tr>
                            )}
                            {hrMonitor.byDept.map((d) => (
                                <tr key={d.department} className="hover:bg-slate-50/80">
                                    <td className="px-4 py-3 font-semibold text-slate-800">{d.department}</td>
                                    <td className="px-4 py-3 text-right">{d.members}</td>
                                    <td className="px-4 py-3 text-right">{d.approvedTasks}</td>
                                    <td className="px-4 py-3 text-right">{d.poolConfirmed}/{d.members}</td>
                                    <td className="px-4 py-3 text-right">{d.peerReports}/{d.members}</td>
                                    <td className={`px-4 py-3 text-right font-extrabold ${d.exRate > 20 ? 'text-rose-600' : 'text-slate-800'}`}>
                                        {d.exRate}%
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
